import type { ViewStyle } from 'react-native'

import { Box, useTheme } from '../theme/restyle'
import { FALLBACK_ASSET_LOGO } from './assetLogos'
import { FunIcon } from './FunIcon'
import { chainIconUri, tokenLogoUri } from './funIconUri'

/**
 * A token logo with a small chain badge overlaid bottom-right (Figma: the
 * `icon/crypto` avatar) — the RN analogue of web's `FunAssetAvatar`. The token
 * icon resolves from `iconSrc` (direct URL, matching web's
 * `DynamicTargetAssetCandidate.iconSrc`) or falls back to CDN via `symbol`
 * ({@link tokenLogoUri}). The badge resolves by CDN chain key. Omit `chainKey`
 * to render the token logo alone.
 */
export interface TokenChainIconProps {
  /** Token symbol → CDN token logo fallback, e.g. `"USDG"`. */
  symbol: string
  /** Direct URL for the token icon — preferred over CDN `symbol` lookup. */
  iconSrc?: string
  /** CDN chain key for the badge overlay, e.g. `"ethereum"`. Omit to hide it. */
  chainKey?: string
  /**
   * Direct URL for the badge — preferred over `chainKey`, and the way to badge
   * by chain id, whose logo comes from `@funkit/chains` rather than the CDN key.
   */
  chainIconSrc?: string
  size?: number
}

export function TokenChainIcon({
  symbol,
  iconSrc,
  chainKey,
  chainIconSrc,
  size = 24,
}: TokenChainIconProps) {
  const theme = useTheme()
  const badge = Math.round(size * 0.5)
  const badgeUri =
    chainIconSrc ?? (chainKey ? chainIconUri(chainKey) : undefined)
  return (
    <Box width={size} height={size}>
      <FunIcon
        uri={iconSrc ?? tokenLogoUri(symbol)}
        size={size}
        fallbackUri={FALLBACK_ASSET_LOGO}
      />
      {badgeUri ? (
        <Box
          borderRadius="rounded"
          alignItems="center"
          justifyContent="center"
          style={[
            badgeRing(badge),
            { backgroundColor: theme.background.modal },
          ]}
        >
          <FunIcon
            uri={badgeUri}
            size={badge}
            fallbackUri={FALLBACK_ASSET_LOGO}
          />
        </Box>
      ) : null}
    </Box>
  )
}

const badgeRing = (badge: number): ViewStyle => ({
  position: 'absolute',
  right: -1,
  bottom: -1,
  width: badge + 2,
  height: badge + 2,
})
